import { invoke } from '../shims/forge-bridge';
import { AIMessage, AIProvider, AIResponse } from './AIProviderService';

export type AsyncGenerationStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'not_found';

export interface AsyncGenerationPayload {
  provider: AIProvider;
  modelName: string;
  messages: AIMessage[];
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  jsonMode?: boolean;
  responseSchema?: Record<string, unknown>;
  requestType?: 'story' | 'enhancer';
}

export interface AsyncGenerationResult<T> {
  requestId: string;
  status: AsyncGenerationStatus;
  result?: T;
  error?: string;
}

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const enqueueGenerationJob = async (
  payload: AsyncGenerationPayload
): Promise<{ requestId: string }> => {
  return (await invoke('enqueueGenerationJob', payload)) as { requestId: string };
};

export const getGenerationJobStatus = async <T>(
  requestId: string
): Promise<AsyncGenerationResult<T>> => {
  return (await invoke('getGenerationJobStatus', { requestId })) as AsyncGenerationResult<T>;
};

export const waitForGenerationResult = async (
  requestId: string,
  options?: { timeoutMs?: number; pollIntervalMs?: number }
): Promise<AIResponse> => {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const status = await getGenerationJobStatus<AIResponse>(requestId);
    if (status.status === 'completed') {
      if (!status.result) {
        throw new Error('Generation completed with no result.');
      }
      return status.result;
    }
    if (status.status === 'failed') {
      throw new Error(status.error || 'Generation failed.');
    }
    if (status.status === 'not_found') {
      throw new Error('Generation job not found.');
    }
    await delay(pollIntervalMs);
  }

  throw new Error('Generation timed out. Please try again.');
};
